<script lang="ts">
  import { api, ApiError } from '../lib/api/client.ts';

  interface Props {
    /** No account exists yet: this is first-run registration, not a login. */
    firstRun: boolean;
    onDone: () => void;
  }
  const { firstRun, onDone }: Props = $props();

  let username = $state('');
  let password = $state('');
  let passwordRepeat = $state('');
  let busy = $state(false);
  let error = $state<string | null>(null);

  async function submit(event: SubmitEvent) {
    event.preventDefault();
    error = null;

    if (firstRun && password !== passwordRepeat) {
      error = 'Die beiden Passwörter stimmen nicht überein.';
      return;
    }

    busy = true;
    try {
      if (firstRun) await api.setupAccount(username.trim(), password);
      else await api.login(username.trim(), password);
      password = '';
      passwordRepeat = '';
      onDone();
    } catch (err) {
      // 429 carries its own wait time; everything else keeps the server wording,
      // which is deliberately identical for wrong user and wrong password.
      error = err instanceof ApiError || err instanceof Error ? err.message : String(err);
    } finally {
      busy = false;
    }
  }
</script>

<div class="login">
  <form class="card" onsubmit={submit}>
    <h1>{firstRun ? 'Konto anlegen' : 'Anmelden'}</h1>

    {#if firstRun}
      <p class="muted">
        Noch kein Konto vorhanden. Lege jetzt deines an — danach ist diese Seite geschlossen
        und nur noch aus deinem lokalen Netz erreichbar.
      </p>
    {/if}

    <label>
      <span>Benutzername</span>
      <input
        bind:value={username}
        data-focusable
        data-focus-default
        autocomplete="username"
        required
      />
    </label>

    <label>
      <span>Passwort</span>
      <input
        bind:value={password}
        data-focusable
        type="password"
        autocomplete={firstRun ? 'new-password' : 'current-password'}
        required
      />
    </label>

    {#if firstRun}
      <label>
        <span>Passwort wiederholen</span>
        <input bind:value={passwordRepeat} data-focusable type="password" autocomplete="new-password" required />
      </label>
      <p class="faint hint">
        Mindestens 12 Zeichen. Länge schützt mehr als Sonderzeichen — eine Passphrase aus
        mehreren Wörtern ist stärker und leichter zu merken.
      </p>
    {/if}

    {#if error}<p class="error">{error}</p>{/if}

    <button data-focusable type="submit" disabled={busy} class="primary">
      {busy ? 'Moment …' : firstRun ? 'Konto anlegen' : 'Anmelden'}
    </button>
  </form>
</div>

<style>
  .login {
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: var(--safe-inset);
  }

  .card {
    width: min(34em, 100%);
    display: flex;
    flex-direction: column;
    gap: var(--pad);
    background: var(--bg-raised);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    padding: calc(var(--pad) * 1.5);
  }

  h1 { margin: 0; font-size: var(--font-xl); }
  p.muted { margin: 0; line-height: 1.5; }

  label { display: flex; flex-direction: column; gap: 6px; }
  label > span { font-size: var(--font-sm); color: var(--text-dim); }

  .hint { margin: 0; font-size: var(--font-sm); line-height: 1.4; }
  .error { margin: 0; color: var(--danger); }

  .primary { background: var(--accent-dim); border-color: var(--accent); font-weight: 600; }
  .primary:disabled { opacity: 0.6; }
</style>
